/* eslint-disable react/prop-types */
"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import AppModalShell from "../common/AppModalShell";
import { getCurrentPosition } from "../../native/runtime";
import {
  LoadingMapSkeleton,
} from "../common/LoadingSkeleton";

const MAP_STYLE_URL =
  "https://api.maptiler.com/maps/0196259d-93a4-75bb-af54-6b003b7377ab/style.json?key=QMa6CZX7KdzCIRISMOjh";
const DEFAULT_CENTER = { lng: 17.1077, lat: 48.1486 };
const LOCATION_REQUEST_WATCHDOG_MS = 15000;
const MAP_LOAD_WATCHDOG_MS = 10000;

function withWatchdog(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    Promise.resolve(promise).then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function SheetPortal({
  open,
  onClose,
  title,
  closeAriaLabel = "Close",
  closeOnBackdropClick = true,
  children,
}) {
  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={title}
      closeAriaLabel={closeAriaLabel}
      maxWidthClassName="max-w-[430px]"
      desktopMaxHeight="min(82vh, 760px)"
      zIndexClassName="z-[13000]"
      closeOnBackdropClick={closeOnBackdropClick}
    >
      {children}
    </AppModalShell>
  );
}

function SheetTextInput({ value, onChange, placeholder, className = "" }) {
  return (
    <input
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      placeholder={placeholder}
      className={`h-[50px] w-full rounded-[46px] border border-white/20 bg-white/10 px-5 text-[16px] font-semibold text-white outline-none placeholder:text-white/40 ${className}`}
    />
  );
}

export function ChatPollSheet({ open, onClose, onSubmit, submitting = false }) {
  const { t } = useTranslation("menu");
  const [title, setTitle] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [caption, setCaption] = useState("");
  const [allowMultiple, setAllowMultiple] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setOptions(["", ""]);
    setCaption("");
    setAllowMultiple(false);
  }, [open]);

  const cleanTitle = title.trim();
  const cleanOptions = options.map((value) => value.trim()).filter(Boolean);
  const canSubmit = cleanTitle && cleanOptions.length >= 2 && !submitting;

  const addOption = () => {
    setOptions((prev) => (prev.length >= 10 ? prev : [...prev, ""]));
  };

  const updateOption = (index, value) => {
    setOptions((prev) => prev.map((item, idx) => (idx === index ? value : item)));
  };

  const submit = async () => {
    if (!canSubmit) return;
    const ok = await onSubmit?.({
      text: "",
      caption: caption.trim(),
      payload: {
        kind: "chat_poll",
        title: cleanTitle,
        allow_multiple: allowMultiple,
        options: cleanOptions.map((textValue) => ({ text: textValue })),
      },
    });
    if (ok !== false) onClose?.();
  };

  return (
    <SheetPortal
      open={open}
      onClose={onClose}
      title={t("chats.event_chat.poll_composer.title")}
      closeAriaLabel={t("chats.event_chat.poll_composer.close")}
    >
      <div>
        <label className="mb-[10px] block text-[24px] font-semibold leading-[29px]">
          {t("chats.event_chat.poll_composer.question_label")}
        </label>
        <SheetTextInput
          value={title}
          onChange={setTitle}
          placeholder={t("chats.event_chat.poll_composer.question_placeholder")}
        />
      </div>

      <div className="mt-[24px]">
        <label className="mb-[10px] block text-[24px] font-semibold leading-[29px]">
          {t("chats.event_chat.poll_composer.options_label")}
        </label>
        <div className="space-y-[10px]">
          {options.map((option, index) => (
            <SheetTextInput
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              value={option}
              onChange={(value) => updateOption(index, value)}
              placeholder={t("chats.event_chat.poll_composer.option_placeholder", {
                index: index + 1,
              })}
              className="pr-14"
            />
          ))}
        </div>

        <button
          type="button"
          onClick={addOption}
          disabled={options.length >= 10}
          className="mt-[8px] flex h-[50px] w-full items-center justify-center gap-3 rounded-[100px] border border-white/20 bg-white/10 text-[18px] font-bold text-white transition active:scale-[0.99] disabled:opacity-45"
        >
          <img src="/assets/menu/add_option.svg" alt="" className="h-6 w-6 object-contain" />
          {t("chats.event_chat.poll_composer.add_option")}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setAllowMultiple((prev) => !prev)}
        className="mt-[18px] flex min-h-[50px] w-full items-center justify-between gap-4 rounded-[24px] border border-white/20 bg-white/10 px-5 py-3 text-left transition active:scale-[0.99]"
        aria-pressed={allowMultiple}
      >
        <span className="min-w-0 text-[16px] font-semibold leading-[20px] text-white">
          {t("chats.event_chat.poll_composer.allow_multiple")}
        </span>
        <span
          className={`flex h-[28px] w-[48px] shrink-0 items-center rounded-full p-[3px] transition ${
            allowMultiple ? "bg-[#3E6FFB]" : "bg-white/18"
          }`}
          aria-hidden="true"
        >
          <span
            className={`h-[22px] w-[22px] rounded-full bg-white shadow-[0_3px_8px_rgba(0,0,0,0.18)] transition-transform ${
              allowMultiple ? "translate-x-[20px]" : "translate-x-0"
            }`}
          />
        </span>
      </button>

      <div className="mt-[20px] flex h-[70px] items-center rounded-[100px] border border-white/25 bg-white/10 px-5">
        <input
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          placeholder={t("chats.event_chat.poll_composer.caption_placeholder")}
          className="min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-white outline-none placeholder:text-white/45"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="ml-3 flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#3E6FFB] transition active:scale-95 disabled:opacity-40"
          aria-label={t("chats.event_chat.poll_composer.send")}
        >
          <img src="/assets/menu/send.svg" alt="" className="h-6 w-6 brightness-0 invert" />
        </button>
      </div>
    </SheetPortal>
  );
}

export function ChatLocationSheet({ open, onClose, onSubmit, submitting = false }) {
  const { t } = useTranslation("menu");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const isOpenRef = useRef(false);
  const locationRequestActiveRef = useRef(false);
  const locationRequestStartedRef = useRef(false);
  const locationRequestIdRef = useRef(0);
  const dismissLockTimerRef = useRef(null);
  const reverseGeocodeRequestRef = useRef(0);
  const selectedCenterRef = useRef(DEFAULT_CENTER);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [address, setAddress] = useState("");
  const [caption, setCaption] = useState("");
  const [locating, setLocating] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [backdropCloseLocked, setBackdropCloseLocked] = useState(false);

  const reverseGeocode = useCallback(async (nextCenter) => {
    const lng = Number(nextCenter?.lng);
    const lat = Number(nextCenter?.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    const requestId = reverseGeocodeRequestRef.current + 1;
    reverseGeocodeRequestRef.current = requestId;

    try {
      const url = `https://api.maptiler.com/geocoding/${lng},${lat}.json?key=QMa6CZX7KdzCIRISMOjh&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      const best = Array.isArray(data?.features) ? data.features[0] : null;
      if (isOpenRef.current && reverseGeocodeRequestRef.current === requestId) {
        setAddress(String(best?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`).trim());
      }
    } catch {
      if (isOpenRef.current && reverseGeocodeRequestRef.current === requestId) {
        setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      }
    }
  }, []);

  const requestCurrentLocation = useCallback(async () => {
    if (locationRequestActiveRef.current) return;

    const requestId = locationRequestIdRef.current + 1;
    locationRequestIdRef.current = requestId;
    locationRequestActiveRef.current = true;
    if (isOpenRef.current) {
      setLocating(true);
      // iOS can replay the tap that dismissed its native permission prompt.
      // Keep the sheet open until that interaction has fully settled.
      setBackdropCloseLocked(true);
    }

    try {
      const position = await withWatchdog(
        getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 9000,
          maximumAge: 30000,
        }),
        LOCATION_REQUEST_WATCHDOG_MS,
        "Location request timed out"
      );
      const next = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      if (!isOpenRef.current || locationRequestIdRef.current !== requestId) return;

      selectedCenterRef.current = next;
      setCenter(next);
      markerRef.current?.setLngLat([next.lng, next.lat]);
      try {
        mapRef.current?.flyTo?.({ center: [next.lng, next.lat], zoom: 16, essential: true });
      } catch {
        // The map can still be initializing while the OS permission alert is open.
      }
      reverseGeocode(next);
    } catch {
      // Keep the picker available when permission is denied or a one-time
      // location request times out. The user can still place the marker.
    } finally {
      if (locationRequestIdRef.current !== requestId) return;
      locationRequestActiveRef.current = false;
      if (isOpenRef.current) {
        setLocating(false);
        if (dismissLockTimerRef.current) window.clearTimeout(dismissLockTimerRef.current);
        dismissLockTimerRef.current = window.setTimeout(() => {
          dismissLockTimerRef.current = null;
          if (isOpenRef.current) setBackdropCloseLocked(false);
        }, 650);
      }
    }
  }, [reverseGeocode]);

  useEffect(() => {
    isOpenRef.current = open;
    if (!open) {
      locationRequestIdRef.current += 1;
      locationRequestActiveRef.current = false;
      locationRequestStartedRef.current = false;
      if (dismissLockTimerRef.current) window.clearTimeout(dismissLockTimerRef.current);
      dismissLockTimerRef.current = null;
      setBackdropCloseLocked(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    setCaption("");
    setAddress("");
    setCenter(DEFAULT_CENTER);
    selectedCenterRef.current = DEFAULT_CENTER;
    setMapLoading(true);
    locationRequestStartedRef.current = false;

    let cancelled = false;
    let map = null;
    let locationTimer = null;
    let mapLoadTimer = null;
    const init = () => {
      if (cancelled || !mapContainerRef.current) return;
      try {
        const initialCenter = selectedCenterRef.current;
        map = new maplibregl.Map({
          container: mapContainerRef.current,
          style: MAP_STYLE_URL,
          center: [initialCenter.lng, initialCenter.lat],
          zoom: 15,
          attributionControl: false,
        });
        mapRef.current = map;
        markerRef.current = new maplibregl.Marker({ draggable: false })
          .setLngLat([initialCenter.lng, initialCenter.lat])
          .addTo(map);
        map.on("load", () => {
          if (cancelled) return;
          if (mapLoadTimer) window.clearTimeout(mapLoadTimer);
          mapLoadTimer = null;
          setMapLoading(false);
          map.resize();
        });
        map.on("error", () => {
          if (cancelled) return;
          if (mapLoadTimer) window.clearTimeout(mapLoadTimer);
          mapLoadTimer = null;
          setMapLoading(false);
        });
        map.on("move", () => {
          const next = map.getCenter();
          const nextCenter = { lng: next.lng, lat: next.lat };
          selectedCenterRef.current = nextCenter;
          markerRef.current?.setLngLat([nextCenter.lng, nextCenter.lat]);
          setCenter(nextCenter);
        });
        reverseGeocode(initialCenter);
        mapLoadTimer = window.setTimeout(() => {
          mapLoadTimer = null;
          if (!cancelled) setMapLoading(false);
        }, MAP_LOAD_WATCHDOG_MS);
        locationTimer = window.setTimeout(() => {
          if (cancelled || locationRequestStartedRef.current) return;
          locationRequestStartedRef.current = true;
          requestCurrentLocation();
        }, 180);
      } catch {
        setMapLoading(false);
      }
    };

    const frame = window.requestAnimationFrame(init);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      if (locationTimer) window.clearTimeout(locationTimer);
      if (mapLoadTimer) window.clearTimeout(mapLoadTimer);
      try {
        mapRef.current?.remove?.();
      } catch {
        // ignore
      }
      mapRef.current = null;
      markerRef.current = null;
      setMapLoading(false);
      setLocating(false);
    };
  }, [open, reverseGeocode, requestCurrentLocation]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => reverseGeocode(center), 700);
    return () => window.clearTimeout(timer);
  }, [center, open, reverseGeocode]);

  const submit = async () => {
    if (submitting) return;
    const lat = Number(center.lat);
    const lng = Number(center.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const label = address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    const ok = await onSubmit?.({
      text: caption.trim() || label,
      payload: {
        kind: "chat_location",
        lat,
        lng,
        address: label,
        caption: caption.trim(),
      },
    });
    if (ok !== false) onClose?.();
  };

  return (
    <SheetPortal
      open={open}
      onClose={onClose}
      title={t("chats.event_chat.location_composer.title")}
      closeAriaLabel={t("chats.event_chat.location_composer.close")}
      closeOnBackdropClick={!backdropCloseLocked}
    >
      <div className="relative h-[335px] overflow-hidden rounded-[26px] bg-[#D9D9D9]">
        <div ref={mapContainerRef} className="h-full w-full" />
        {mapLoading ? (
          <LoadingMapSkeleton />
        ) : null}
        <button
          type="button"
          onClick={requestCurrentLocation}
          disabled={locating}
          aria-busy={locating}
          className="absolute bottom-[92px] right-[19px] flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#3E6FFB] shadow-[0_10px_22px_rgba(62,111,251,0.35)] transition active:scale-95 disabled:opacity-65"
          aria-label={t("chats.event_chat.location_composer.use_current")}
        >
          <img
            src="/assets/menu/location_icon.svg"
            alt=""
            className={`h-[29px] w-[29px] brightness-0 invert ${locating ? "opacity-70" : ""}`}
          />
        </button>
        <div className="absolute bottom-[18px] left-1/2 flex h-[50px] w-[calc(100%-36px)] -translate-x-1/2 items-center justify-center rounded-[25px] border border-white/25 bg-black/50 px-4 text-center text-[13px] font-semibold text-white">
          <span className="truncate">{address || `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`}</span>
        </div>
      </div>

      <div className="mt-[20px] flex h-[70px] items-center rounded-[100px] border border-white/25 bg-white/10 px-5">
        <input
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          placeholder={t("chats.event_chat.location_composer.caption_placeholder")}
          className="min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-white outline-none placeholder:text-white/45"
        />
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="ml-3 flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#3E6FFB] transition active:scale-95 disabled:opacity-40"
          aria-label={t("chats.event_chat.location_composer.send")}
        >
          <img src="/assets/menu/send.svg" alt="" className="h-6 w-6 brightness-0 invert" />
        </button>
      </div>
    </SheetPortal>
  );
}
